/**
 * Generation Units API (Anlagen)
 * CRUD-Operationen für Windenergieanlagen pro Projekt
 */
import { api } from "./client";
import { GenerationUnit, GenerationUnitCreate } from "../../types";
import {
  BetriebsmodusCreate,
  BetriebsmodusFelder,
  StoffCreate,
  StoffFelder,
  StoffUpdate,
} from "./anlagentypen";

/** Ein Stoff der projektlokalen Kopie an einer Anlage */
export interface GenerationUnitStoff extends StoffFelder {
  id: string;
  generation_unit_id: string;
}

/**
 * Die Betriebsangaben aus ELiA 4.5 – es gibt sie nur an der Anlage.
 *
 * Kein Kennzeichen „wird gefahren": In ELiA existiert eine Zeile in 4.5 genau dann, wenn der
 * Betriebszustand gefahren wird. Ein nicht gefahrener Modus wird an der Anlage gelöscht.
 */
export interface BetriebsangabenFelder {
  einsatzzeit_tage?: number | null;
  einsatzzeit_bezug_id?: string | null;
  einsatzzeit_stunden_pro_tag?: number | null;
  einsatzzeit_zeitfenster?: string | null;
}

export type BetriebsangabenUpdate = Partial<BetriebsangabenFelder>;

/** Vermessung vom Anlagentyp, Betriebsangaben aus dem Projekt */
export interface GenerationUnitBetriebsmodus
  extends BetriebsmodusFelder, BetriebsangabenFelder {
  id: string;
  generation_unit_id: string;
}

/** Technische Daten eines Anlagentyps zur Vorbefüllung */
export interface TypOption {
  typ: string;
  rotorradius?: number;
  sicherungsradius?: number;
  nennleistung?: number;
  referenzertrag?: number;
  nabenhöhe?: number;
}

/** Hersteller → Typ-Optionen (mit technischen Daten zur Vorbefüllung) */
export type HerstellerTypOptions = Record<string, TypOption[]>;

export const generationUnitsApi = {
  /**
   * Holt Hersteller → Typ-Mapping mit technischen Daten zur Vorbefüllung
   * Struktur: { "Vestas": [{ typ, rotorradius, ... }, ...], "Nordex": [...], ... }
   */
  async getGenerationUnitOptions(): Promise<HerstellerTypOptions> {
    return api.get("/generation-units/options");
  },

  async getProjectGenerationUnits(
    projectId: string,
  ): Promise<GenerationUnit[]> {
    return api.get(`/projects/${projectId}/generation-units`);
  },

  /**
   * Erstellt eine oder mehrere Anlagen für ein Projekt
   * Bei Anzahl > 1 wird die gleiche Vorlage mehrfach in die Liste eingefügt
   */
  async createGenerationUnits(
    projectId: string,
    items: GenerationUnitCreate[],
  ): Promise<GenerationUnit[]> {
    return api.post(`/projects/${projectId}/generation-units`, items);
  },

  async updateGenerationUnit(
    projectId: string,
    unitId: string,
    data: Partial<GenerationUnitCreate>,
  ): Promise<GenerationUnit> {
    return api.put(`/projects/${projectId}/generation-units/${unitId}`, data);
  },

  async deleteGenerationUnit(projectId: string, unitId: string): Promise<void> {
    return api.delete(`/projects/${projectId}/generation-units/${unitId}`);
  },

  // --- Projektlokale Kopien der Anlagentyp-Listen ---
  //
  // Sie entstehen beim Anlegen der Anlage als Kopie der Vorlage und sind danach im Projekt
  // pflegbar. Aenderungen wirken nie auf den Anlagentyp zurueck.

  async getStoffe(
    projectId: string,
    unitId: string,
  ): Promise<GenerationUnitStoff[]> {
    return api.get(`/projects/${projectId}/generation-units/${unitId}/stoffe`);
  },

  async createStoff(
    projectId: string,
    unitId: string,
    data: StoffCreate,
  ): Promise<GenerationUnitStoff> {
    return api.post(
      `/projects/${projectId}/generation-units/${unitId}/stoffe`,
      data,
    );
  },

  async updateStoff(
    projectId: string,
    unitId: string,
    stoffId: string,
    data: StoffUpdate,
  ): Promise<GenerationUnitStoff> {
    return api.put(
      `/projects/${projectId}/generation-units/${unitId}/stoffe/${stoffId}`,
      data,
    );
  },

  async deleteStoff(
    projectId: string,
    unitId: string,
    stoffId: string,
  ): Promise<void> {
    return api.delete(
      `/projects/${projectId}/generation-units/${unitId}/stoffe/${stoffId}`,
    );
  },

  async getBetriebsmodi(
    projectId: string,
    unitId: string,
  ): Promise<GenerationUnitBetriebsmodus[]> {
    return api.get(
      `/projects/${projectId}/generation-units/${unitId}/betriebsmodi`,
    );
  },

  /**
   * Legt einen Betriebsmodus an, den nur dieses Projekt führt. Der Regelfall ist die Kopie aus
   * der Vorlage; schreibt die Genehmigung aber einen Modus vor, den der Anlagentyp noch nicht
   * kennt, entsteht er hier.
   */
  async createBetriebsmodus(
    projectId: string,
    unitId: string,
    data: BetriebsmodusCreate & BetriebsangabenFelder,
  ): Promise<GenerationUnitBetriebsmodus> {
    return api.post(
      `/projects/${projectId}/generation-units/${unitId}/betriebsmodi`,
      data,
    );
  },

  async deleteBetriebsmodus(
    projectId: string,
    unitId: string,
    modusId: string,
  ): Promise<void> {
    return api.delete(
      `/projects/${projectId}/generation-units/${unitId}/betriebsmodi/${modusId}`,
    );
  },

  async updateBetriebsmodus(
    projectId: string,
    unitId: string,
    modusId: string,
    data: BetriebsangabenUpdate & Partial<BetriebsmodusFelder>,
  ): Promise<GenerationUnitBetriebsmodus> {
    return api.put(
      `/projects/${projectId}/generation-units/${unitId}/betriebsmodi/${modusId}`,
      data,
    );
  },
};
